import { createRoot } from 'react-dom/client';

import { CookieConsentApp } from './CookieConsentApp';
import { getConsent } from './lib/cookieConsent';
import { applyThirdPartyScripts } from './lib/thirdParty';
import { CONSENT_CHANGED_EVENT, DEFAULT_POLICY_VERSION } from './lib/cookieConsent';
import type { CookieConsentConfig } from './types/cookies.types';

declare global {
  interface Window {
    __INITWIN_COOKIE_CONFIG__?: Partial<CookieConsentConfig>;
  }
}

const defaultConfig: CookieConsentConfig = {
  policyVersion: DEFAULT_POLICY_VERSION,
  cookiesPolicyUrl: '/politica-cookies',
  gaTrackingId: '',
  facebookPixelId: '',
};

/**
 * Mounts cookie consent on public pages when `#cookie-consent-root` exists.
 */
function mountCookieConsent(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const el = document.getElementById('cookie-consent-root');
  if (!el) {
    return;
  }

  const injected = window.__INITWIN_COOKIE_CONFIG__ ?? {};
  const config: CookieConsentConfig = {
    ...defaultConfig,
    ...injected,
    ui: { ...defaultConfig.ui, ...injected.ui },
    categories: injected.categories ?? defaultConfig.categories,
    policyVersion: injected.policyVersion ?? DEFAULT_POLICY_VERSION,
  };

  const existing = getConsent(config.policyVersion);
  if (existing) {
    applyThirdPartyScripts(existing, config);
  }

  try {
    createRoot(el).render(
      <CookieConsentApp config={config} showFloatingSettings />,
    );
  } catch (err) {
    console.error('[cookie-consent] mount failed', err);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountCookieConsent);
} else {
  mountCookieConsent();
}

export { CookieConsentApp } from './CookieConsentApp';
export {
  getConsent,
  saveConsent,
  hasConsent,
  isAllowed,
  buildConsentState,
} from './lib/cookieConsent';
export { CONSENT_CHANGED_EVENT } from './types/cookies.types';
export type { ConsentState, CookieCategory, CookieConsentConfig } from './types/cookies.types';
